<template>
  <v-row>
    <v-col>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        :label="$t('transaction.listing.search.label')"
        single-line
        hide-details
      />
    </v-col>
    <v-col cols="12">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="elevation-1 col-12"
      >
        <template v-slot:[`item.tran_ref`]="{ item }">
          {{ item.tran_ref }}
        </template>
        <template v-slot:[`item.tran_date`]="{ item }">
          <DateFormat
            :date="item.tran_date"
            :showtime="true"
          />
        </template>
        <template v-slot:[`item.tran_type`]="{ item }">
          <TransactionTypeLabel
            :transaction-type="item.tran_type"
          />
        </template>
        <template v-slot:[`item.amount`]="{ item }">
          <b style="text-align: right;">
            <span>{{ item.currency }}</span>
            {{ (parseFloat(item.amount)).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') }}
          </b>
        </template>
        <template v-slot:[`item.names`]="{ item }">
          <div class="my-2">
            <router-link
              style="color: #3A3A3A;font-size: 16px; line-height: 21px;"
              color="primary"
              :to="{ name: 'NaturalPersonEdit', params: { np_id: item.person.id } }"
              v-if="item.person"
              :data-cy="`to_NaturalPersonEdit_${item.person.id}`"
            >
              <div class="my-1">
                {{ item.person | natural_person_name(1, 1) }}
              </div>
              <div class="my-1">
                {{ item.person | natural_person_name(2, 1) }}
              </div>
            </router-link>
            <router-link
              style="color: #3A3A3A;font-size: 16px; line-height: 21px;"
              color="primary"
              :to="{ name: 'CompanyEdit', params: { customer_id: item.company.id } }"
              v-else-if="item.company"
              :data-cy="`to_CompanyEdit_${item.company.id}`"
            >
              <div class="my-1">
                {{ item.company | company_name(1) }}
              </div>
              <div class="my-1">
                {{ item.company | company_name(2) }}
              </div>
            </router-link>
            <span v-else>(Deleted)</span>
            <div
              style="color: #757575;font-size: 12px;line-height: 16px;"
            >
              {{ item.person ? 'Natural Person' : (item.company ? 'Company' : '') }}
            </div>
          </div>
        </template>
        <template v-slot:[`item.flags`]="{ item }">
          <TransactionCDDFlag :item="item" />
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                dark
                width="30"
                height="30"
                :style="{
                  'background-color': (item?.person ?? item?.company)?.established_business_relationship ? '#2C6CD3' : '#A1A1A4',
                }"
                v-bind="attrs"
                v-on="on"
              >
                <v-img
                  dark
                  :src="icons.business_relationship"
                  width="20"
                  max-height="13"
                  contain
                />
              </v-btn>
            </template>
            <span>
              {{
                (item?.person ?? item?.company)?.established_business_relationship
                  ? $t('transaction.details.established_business_relationship.options[1]')
                  : $t('transaction.details.established_business_relationship.options[0]')
              }}
            </span>
          </v-tooltip>
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <div>
            <v-btn
              icon
              :to="{ name: 'Transaction3Edit', params: { transaction_id: item.id } }"
              :data-cy="`edit_tran_btn_${item.id}`"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <PopupDelete
              v-slot:default="{ on, loading }"
              @yes="(done) => {
                $store.dispatch('transaction/remove', item.id)
                  .then(() => {
                    getTransactions()
                  })
                  .catch((error) => { console.warn(error) })
                  .finally(done); // notify popup stop loading and close
              }"
              :data-cy="`tran_${item.id}`"
            >
              <v-btn
                icon
                class="mr-1"
                color="error"
                v-on="on"
                :loading="loading"
                :data-cy="`remove_tran_btn_${item.id}`"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </PopupDelete>
          </div>
        </template>
      </v-data-table>
      <v-dialog
        v-model="dialog_tran_detail"
        max-width="90%"
      >
        <v-card>
          <PopupTranDetail
            :records="dialog_tran_detail_record"
            @onSave="(form) => {
              this.getTransactions()
              dialog_tran_detail = false;
              dialog_tran_detail_record = null;
            }"
          />
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import DateFormat from '@/components/common/DateFormat'
import PopupDelete from '@/components/popups/PopupDeleteConfirmation'
import TransactionTypeLabel from './TransactionTypeLabel.vue'
import PopupTranDetail from '@/components/popups/PopupTranDetail2'
import TransactionCDDFlag from './TransactionCDDFlag.vue'
// images
import svgCDD from '@/assets/images/transaction/ic_tx_cdd.svg'
import svgBusinessRelationship from '@/assets/images/transaction/ic_tx_business_relationship.svg'
import { mapGetters } from 'vuex'

export default {
  components: {
    DateFormat,
    PopupDelete,
    TransactionTypeLabel,
    PopupTranDetail,
    TransactionCDDFlag
  },
  props: {
    personId: {
      type: String,
      default: null
    },
    companyId: {
      type: String,
      default: null
    }
  },
  computed: {
    headers () {
      return [
        { text: this.$t('transaction.details.tran_ref.label'), align: 'start', value: 'tran_ref', width: '13%' },
        { text: this.$t('transaction.details.tran_date.label'), value: 'tran_date', width: '13%' },
        { text: this.$t('transaction.details.tran_type.label'), value: 'tran_type', width: '13%' },
        { text: this.$t('transaction.listing.client.label'), align: 'start', sortable: false, value: 'names' },
        { text: this.$t('transaction.details.amount.label'), value: 'amount', width: '13%' },
        { text: '', sortable: false, value: 'flags', width: '120px' },
        { text: this.$t('listing.table.action.label'), sortable: false, value: 'action', width: '120px' }
      ]
    },
    options: {
      get () {
        return this.$store.state.transaction.table.options
      },
      set (_options) {
        this.$store.commit('transaction/SET_TABLE_OPTIONS', _options)
      }
    },
    icons () {
      return {
        cdd: svgCDD,
        business_relationship: svgBusinessRelationship
      }
    },
    ...mapGetters('transaction', [
      'transactionTypesOptions'
    ]),
    getTableType () {
      return this.personId ? 'natural_person' : (this.companyId ? 'company' : 'all')
    }
  },
  watch: {
    options: {
      handler () {
        this.getTransactions()
      },
      deep: true
    },
    search: {
      handler () {
        this.options.page = 1
        this.getTransactions()
      }
    }
  },
  data: () => {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      search: '',
      dialog_tran_detail_record: null,
      dialog_tran_detail: false
    }
  },
  methods: {
    getTransactions () {
      this.loading = true

      const criteria = {
        // populate: 'company,person,alert_case,attachments',
        limit: this.options.itemsPerPage,
        skip: (parseInt(this.options.page) - 1) * this.options.itemsPerPage,
        sort: this.options.sortBy.map((sortBy, key) => {
          return `${sortBy} ${this.options.sortDesc[key] ? 'desc' : 'asc'}`
        }),
        where: {
          ...this.personId || this.companyId
            ? {
              [this.personId ? 'person' : 'company']: this.personId || this.companyId
            }
            : {},
          ...this.search
            ? {
              or: [
                { tran_ref: { contains: this.search } },
                { tran_type: { contains: this.search } },
                parseFloat(this.search)
                  ? { amount: parseFloat(this.search) }
                  : null,
                { currency: { contains: this.search } }
                // ...(() => {
                //   // Check if search is a valid date
                //   const date = new Date(this.search)
                //   const isValidDate = date.toString() !== 'Invalid Date'
                //   const isYear = this.search.match(/^\d{4}[-/\\]?$/)
                //   const isMonth = this.search.match(/^\d{4}[-/\\]\d{2}[-/\\]?$/)
                //   const isDay = this.search.match(/^\d{4}[-/\\]\d{2}[-/\\]\d{2}$/)
                //   return isValidDate
                //     ? [
                //       // same year
                //       isYear ? { tran_date: { '>=': new Date(date.getFullYear(), 0, 1).getTime(), '<=': new Date(date.getFullYear(), 11, 31).getTime() } } : null,
                //       // same month
                //       isMonth ? { tran_date: { '>=': new Date(date.getFullYear(), date.getMonth(), 1).getTime(), '<=': new Date(date.getFullYear(), date.getMonth() + 1, 0).getTime() } } : null,
                //       // same day
                //       isDay ? { tran_date: { '>=': new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime(), '<=': new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime() } } : null
                //     ].filter(Boolean)
                //     : null
                // })()
              ].filter(Boolean)
            }
            : {}
        }
      }

      Promise.all([
        this.$store.dispatch('transaction/findWhere', criteria),
        this.$store.dispatch('transaction/count', { where: criteria.where })
      ]).then(([response, count]) => {
        this.items = response.data
        this.totalItems = count.data.count
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    }
  },
  mounted () {
    this.getTransactions()
  }
}
</script>
